import * as Sentry from '@sentry/react';
import { Box, Button, Typography } from '@mui/material';
import React, { ErrorInfo, ReactNode } from 'react';

import { isChunkLoadError, reloadForStaleChunk } from 'infrastructure/utils/chunkLoadError';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

// Top-level fallback for any render error reaching the route tree. Triggers a
// debounced reload on stale-chunk failures (GF-888) and reports anything else
// to Sentry so it doesn't blank the page silently.
class RouteErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isChunkLoadError(error)) {
      reloadForStaleChunk();
      return;
    }
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Something went wrong loading this page.
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default RouteErrorBoundary;
